import { useEffect, useMemo, useRef, useState } from 'react';
import type { Sector } from '../../types';
import {
  type ChunkStatus,
  type IntelChunk,
  useIntelChunks,
  useIntelMeetingDates,
} from '../../hooks/useIntelChunks';
import { useAgendaMeetings } from '../../hooks/useAgenda';
import { ChunkPreview } from './ChunkPreview';
import { MeetingDateMultiSelect } from './MeetingDateMultiSelect';
import { NewChunkModal } from './NewChunkModal';
import { nextMeetingForSector } from './nextMeeting';
import { SectorMultiSelect } from './SectorMultiSelect';
import styles from './IntelPage.module.css';

interface Props {
  sectors: Sector[];
}

const QUADRANT_LABEL_SHORT: Record<IntelChunk['quadrant'], string> = {
  market_trends: 'Market',
  news_regulatory: 'News',
  transactions: 'Transactions',
  capital_markets: 'Capital',
};

const QUADRANT_COLOR: Record<IntelChunk['quadrant'], string> = {
  market_trends: '#5F5E5A',
  news_regulatory: '#BA7517',
  transactions: '#185FA5',
  capital_markets: '#639922',
};

const PAGE_SIZE = 50;

function nextMondayIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay();
  const offset = dow === 1 ? 0 : ((8 - dow) % 7 || 7);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function formatEventDate(iso: string): string {
  // "Apr 13, 2026" — short month for the compact list row
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function IntelPage({ sectors }: Props) {
  const sortedSectors = useMemo(() => {
    const active = sectors.filter((s) => s.stage === 'active');
    const others = sectors.filter((s) => s.stage !== 'active');
    return [...active, ...others];
  }, [sectors]);

  // Filter state.
  const [selectedSectorIds, setSelectedSectorIds] = useState<Set<number>>(new Set());
  // Meeting-date multi-select (week_of filter).
  const [selectedMeetings, setSelectedMeetings] = useState<Set<string>>(new Set());
  // Activity-date range — filters on event_date (fallback created_at).
  const [activityFrom, setActivityFrom] = useState<string>('');
  const [activityTo, setActivityTo] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<ChunkStatus | ''>('');
  const [textQuery, setTextQuery] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [offset, setOffset] = useState(0);

  // Debounce the text query so we don't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(textQuery), 250);
    return () => clearTimeout(handle);
  }, [textQuery]);

  // Reset to first page whenever any filter changes.
  const filterFingerprint = useMemo(
    () => [
      [...selectedSectorIds].sort().join(','),
      [...selectedMeetings].sort().join(','),
      activityFrom,
      activityTo,
      statusFilter,
      debouncedQuery,
    ].join('|'),
    [selectedSectorIds, selectedMeetings, activityFrom, activityTo, statusFilter, debouncedQuery],
  );
  const lastFingerprintRef = useRef<string>(filterFingerprint);
  if (lastFingerprintRef.current !== filterFingerprint) {
    lastFingerprintRef.current = filterFingerprint;
    if (offset !== 0) setOffset(0);
  }

  const { data: meetingDateOptions } = useIntelMeetingDates();

  const { data: page, isLoading, isFetching, error } = useIntelChunks({
    sectorIds: [...selectedSectorIds],
    meetingDates: [...selectedMeetings],
    activityFrom: activityFrom || null,
    activityTo: activityTo || null,
    q: debouncedQuery || null,
    status: statusFilter || null,
    limit: PAGE_SIZE,
    offset,
  });

  // Selected chunk id (kept stable across page navigations; if the chunk
  // disappears from the current page, the preview shows empty).
  const [selectedChunkId, setSelectedChunkId] = useState<number | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  // Dirty state from the right-pane editor — used to guard selection changes.
  const [previewDirty, setPreviewDirty] = useState(false);

  // Future-meeting picker for the chunk editor — pull a healthy horizon so
  // analysts can attribute chunks to any planned meeting.
  const meetingsHorizonStart = useMemo(() => nextMondayIso(), []);
  const { data: agendaMeetings } = useAgendaMeetings(meetingsHorizonStart, 26);
  const meetingDates = useMemo(
    () => (agendaMeetings ?? []).map((m) => m.meeting_date),
    [agendaMeetings],
  );
  const defaultWeekForSector = useMemo(
    () =>
      (sectorId: number): string => {
        const sector = sortedSectors.find((s) => s.id === sectorId);
        return (
          nextMeetingForSector(agendaMeetings, sectorId, sector) ??
          meetingDates[0] ??
          nextMondayIso()
        );
      },
    [agendaMeetings, meetingDates, sortedSectors],
  );

  /** Wraps setSelectedChunkId so navigating away from a dirty editor
   *  prompts before discarding. Pass `null` to deselect. */
  const requestSelect = (id: number | null) => {
    if (id === selectedChunkId) return;
    if (previewDirty && !confirm('Discard unsaved changes?')) return;
    setSelectedChunkId(id);
  };

  // Auto-select the first chunk when results arrive and nothing is selected.
  const items = page?.items ?? [];
  useEffect(() => {
    if (selectedChunkId == null && items.length > 0) {
      setSelectedChunkId(items[0].id);
    }
  }, [items, selectedChunkId]);

  const selectedChunk = useMemo(
    () => items.find((c) => c.id === selectedChunkId) ?? null,
    [items, selectedChunkId],
  );

  const total = page?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  const filtersActive =
    selectedSectorIds.size > 0 ||
    selectedMeetings.size > 0 ||
    activityFrom !== '' ||
    activityTo !== '' ||
    statusFilter !== '' ||
    debouncedQuery !== '';

  const clearFilters = () => {
    setSelectedSectorIds(new Set());
    setSelectedMeetings(new Set());
    setActivityFrom('');
    setActivityTo('');
    setStatusFilter('');
    setTextQuery('');
  };

  // For the "new chunk" defaults — pick the first selected sector if any,
  // else the first active sector.
  const defaultSectorId =
    selectedSectorIds.size > 0
      ? [...selectedSectorIds][0]
      : sortedSectors[0]?.id ?? 0;

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <span className={styles.headerLabel}>Intelligence</span>
            <span className={styles.headerSub}>
              All sector intelligence chunks · approved chunks land on Monday's slides
            </span>
          </div>
          <button
            className={styles.newBtn}
            onClick={() => setCreatingNew(true)}
            disabled={!defaultSectorId}
            title="Draft from a URL or write a chunk manually"
          >
            + New chunk
          </button>
        </div>

        <div className={styles.filterBar}>
          <SectorMultiSelect
            sectors={sortedSectors}
            selected={selectedSectorIds}
            onChange={setSelectedSectorIds}
          />
          <MeetingDateMultiSelect
            dates={meetingDateOptions ?? []}
            selected={selectedMeetings}
            onChange={setSelectedMeetings}
          />
          <div
            className={styles.dateGroup}
            title="Published or created date — matches event_date (if set) or created_at"
          >
            <label className={styles.dateLabel}>
              <span>Published/Created From</span>
              <input
                type="date"
                className={styles.dateInput}
                value={activityFrom}
                onChange={(e) => setActivityFrom(e.target.value)}
              />
            </label>
            <label className={styles.dateLabel}>
              <span>To</span>
              <input
                type="date"
                className={styles.dateInput}
                value={activityTo}
                onChange={(e) => setActivityTo(e.target.value)}
              />
            </label>
          </div>
          <select
            className={styles.statusSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter((e.target.value as ChunkStatus | '') || '')}
            aria-label="Status filter"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="approved">Approved</option>
            <option value="excluded">Excluded</option>
          </select>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search lead, body, tags…"
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
          />
          {filtersActive && (
            <button className={styles.clearBtn} onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>

        <div className={styles.split}>
          <div className={styles.listCol}>
            <div className={styles.listHeader}>
              <span className={styles.listCount}>
                {total === 0
                  ? 'No chunks match'
                  : `Showing ${pageStart}–${pageEnd} of ${total}`}
                {isFetching && total > 0 && <span className={styles.fetchingDot}>…</span>}
              </span>
              <div className={styles.pager}>
                <button
                  className={styles.pageBtn}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={!hasPrev || isFetching}
                  aria-label="Previous page"
                >
                  ← Prev
                </button>
                <button
                  className={styles.pageBtn}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={!hasNext || isFetching}
                  aria-label="Next page"
                >
                  Next →
                </button>
              </div>
            </div>

            <div className={styles.listBody}>
              {isLoading ? (
                <div className={styles.listEmpty}>Loading…</div>
              ) : error ? (
                <div className={styles.listError}>Error: {error.message}</div>
              ) : items.length === 0 ? (
                <div className={styles.listEmpty}>
                  {filtersActive
                    ? 'No chunks match the current filters.'
                    : 'No chunks yet. Click + New chunk to draft one.'}
                </div>
              ) : (
                items.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`${styles.listRow} ${c.id === selectedChunkId ? styles.listRowActive : ''}`}
                    onClick={() => requestSelect(c.id)}
                  >
                    <div className={styles.rowMetaLine}>
                      <span className={styles.rowSector}>{c.sector_name ?? `Sector ${c.sector_id}`}</span>
                      <span
                        className={styles.rowQuadrant}
                        style={{ color: QUADRANT_COLOR[c.quadrant] }}
                      >
                        {QUADRANT_LABEL_SHORT[c.quadrant]}
                      </span>
                      <span className={`${styles.rowStatus} ${styles[`status_${c.status}`] ?? ''}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className={styles.rowDateLine}>{c.week_of}</div>
                    <div className={styles.rowLead}>
                      {c.bold_lead || <span className={styles.rowLeadEmpty}>(no lead)</span>}
                    </div>
                    {c.body && (
                      <div className={styles.rowBody}>
                        {c.event_date && (
                          <span className={styles.rowDateline}>
                            {formatEventDate(c.event_date)} —{' '}
                          </span>
                        )}
                        {c.body}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className={styles.previewCol}>
            <ChunkPreview
              chunk={selectedChunk}
              sectors={sortedSectors}
              meetingDates={meetingDates}
              onAfterDelete={() => setSelectedChunkId(null)}
              onDirtyChange={setPreviewDirty}
            />
          </div>
        </div>
      </div>

      {creatingNew && defaultSectorId > 0 && (
        <NewChunkModal
          sectors={sortedSectors}
          defaultSectorId={defaultSectorId}
          defaultWeekOf={defaultWeekForSector(defaultSectorId)}
          meetingDates={meetingDates}
          getDefaultWeekForSector={defaultWeekForSector}
          onClose={() => setCreatingNew(false)}
          onCreated={(c) => setSelectedChunkId(c.id)}
        />
      )}
    </div>
  );
}
